import React, { useState } from "react";
import { observer } from "mobx-react";
import Searchbar from "@search/Searchbar";
import SearchForm from "@search/SearchForm";
import * as filters from "@search/filters";
import { TicketsSearchFilterManager } from "./TicketsSearchFilterManager";
import { store } from "@store";
import { TicketsFiltersList } from "@tickets/utils";
import { TodoPriority, TodoStatus, useGetUserSelectUsersQuery } from "@graphql";
import locale from "@bonliva-crm/locale";
import { TodoTags } from "@bonliva-crm/common";
import { useUserProfile } from "@hooks";

const TICKETS_STATUSES_OPTIONS = Object.values(TodoStatus).toLabelValue(
  (x) => locale.tickets.statuses[x]
);

const TICKETS_TAGS_OPTIONS = [
  {
    label: locale.noTag,
    value: "null"
  },
  ...Object.values(TodoTags).toLabelValue()
];

type Props = {
  manager: TicketsSearchFilterManager;
};

export function TicketsSearchForm(props: Props): React.ReactElement<Props> {
  const [users, setUsers] = useState<LabelValue[]>([]);
  const [teamMates, setTeamMates] = useState<LabelValue[]>([]);
  const { myProfile } = useUserProfile();
  const { manager } = props;

  useGetUserSelectUsersQuery({
    onCompleted(data) {
      const users = [];
      const teamMates = [];
      const myTeams = myProfile.Teams ?? [];

      for (const user of data.users) {
        if (user.Teams.some((team) => myTeams.includes(team))) {
          teamMates.push({
            label: user.Name,
            value: user._id
          });
        }

        users.push({
          label: user.Name,
          value: user._id
        });
      }

      setUsers(users);
      setTeamMates(teamMates);
    }
  });

  const teams = store().todoStore.teamsForTodo.map((x) => ({
    label: x.label,
    value: `${x.value}_team`
  }));

  const searchBar = (
    <Searchbar
      onChange={(v) => manager.updateFilter({ text: v })}
      value={manager.filter.text}
    />
  );

  const createdByFilter = filters.renderMultiSelect(
    locale.tickets.createdBy,
    [
      { label: "System", value: "5adb6ee95000000000000001" },
      { label: "Not System", value: "not_5adb6ee95000000000000001" },
      ...teamMates
    ],
    false,
    manager.filter.createdByData,
    (v) =>
      manager.updateFilter({
        createdByData: v
      }),
    false,
    manager.isFilterDirty(TicketsFiltersList.CreatedBy)
  );

  const delegatedToFilter = filters.renderMultiSelect(
    locale.tickets.delegatedBy,
    teamMates,
    false,
    manager.filter.delegatedBy,
    (v) =>
      manager.updateFilter({
        delegatedBy: v
      }),
    manager.isFilterDisabled(TicketsFiltersList.DelegatedBy),
    manager.isFilterDirty(TicketsFiltersList.DelegatedBy)
  );

  const assignedToFilter = filters.renderMultiSelect(
    locale.tickets.assignedTo,
    [...teams, ...teamMates],
    false,
    manager.filter.assignedTo,
    (v) =>
      manager.updateFilter({
        assignedTo: v
      }),
    manager.isFilterDisabled(TicketsFiltersList.AssignedTo),
    manager.isFilterDirty(TicketsFiltersList.AssignedTo)
  );

  const managedByFilter = filters.renderMultiSelect(
    locale.tickets.managedBy,
    users,
    false,
    manager.filter.managedBy,
    (v) =>
      manager.updateFilter({
        managedBy: v
      }),
    manager.isFilterDisabled(TicketsFiltersList.ManagedBy),
    manager.isFilterDirty(TicketsFiltersList.ManagedBy)
  );

  const createdDateFromFilter = filters.renderFrom(
    manager.filter.createdDate.from,
    (v) =>
      manager.updateFilter({
        createdDate: {
          ...manager.filter.createdDate,
          from: v
        }
      }),
    manager.isFilterDirty("createdDate"),
    manager.filter.createdDate.to,
    manager.isFilterDisabled("createdDate"),
    locale.tickets.createdDateFrom
  );

  const createdDateToFilter = filters.renderTo(
    manager.filter.createdDate.to,
    (v) =>
      manager.updateFilter({
        createdDate: {
          ...manager.filter.createdDate,
          to: v
        }
      }),
    manager.isFilterDirty("createdDate"),
    manager.filter.createdDate.from,
    manager.isFilterDisabled("createdDate"),
    locale.tickets.createdDateTo
  );

  const dueDateFromFilter = filters.renderFrom(
    manager.filter.dueDate.from,
    (v) =>
      manager.updateFilter({
        dueDate: {
          ...manager.filter.dueDate,
          from: v
        }
      }),
    manager.isFilterDirty("dueDate"),
    manager.filter.dueDate.to,
    manager.isFilterDisabled("dueDate"),
    locale.tickets.dueDateFrom
  );

  const dueDateToFilter = filters.renderTo(
    manager.filter.dueDate.to,
    (v) =>
      manager.updateFilter({
        dueDate: {
          ...manager.filter.dueDate,
          to: v
        }
      }),
    manager.isFilterDirty("dueDate"),
    manager.filter.dueDate.from,
    manager.isFilterDisabled("dueDate"),
    locale.tickets.dueDateTo
  );

  // const completionStatusFilter = useMemo(
  //   () =>
  //     filters.renderSwitch(
  //       locale.tickets.completionStatus,
  //       manager.filter.completed,
  //       v => manager.updateFilter({ completed: v }),
  //       manager.isFilterDirty("completed")
  //     ),
  //   [manager.filter.completed]
  // );

  const tagsFilter = filters.renderMultiSelect(
    locale.tickets.tags,
    TICKETS_TAGS_OPTIONS,
    true,
    manager.filter.tags,
    (v) =>
      manager.updateFilter({
        tags: v
      }),
    false,
    manager.isFilterDirty(TicketsFiltersList.Tags)
  );

  const priorityFilter = filters.renderSelect(
    locale.tickets.priority,
    [
      { label: "Ingen prioritet", value: null },
      { label: "Låg prioritet", value: "Low" },
      { label: "Hög prioritet", value: "High" }
    ],
    false,
    manager.filter.priority,
    (v) =>
      manager.updateFilter({
        priority: v as TodoPriority
      }),
    false,
    manager.isFilterDirty(TicketsFiltersList.Priority)
  );

  const statusFilter = filters.renderMultiSelect(
    locale.tickets.status,
    TICKETS_STATUSES_OPTIONS,
    false,
    manager.filter.status,
    (v) =>
      manager.updateFilter({
        status: v as TodoStatus[]
      }),
    false,
    manager.isFilterDirty(TicketsFiltersList.Status)
  );

  return (
    <SearchForm searchbar={searchBar}>
      {createdByFilter} {delegatedToFilter} {assignedToFilter} {managedByFilter}
      {createdDateFromFilter}
      {createdDateToFilter}
      {dueDateFromFilter} {dueDateToFilter} {statusFilter} {tagsFilter} {priorityFilter}
    </SearchForm>
  );
}

export default observer(TicketsSearchForm);
